/**
 * CityOverlay.js
 *
 * Phase 9 — Cities & Settlements interaction modal.
 *
 * Opens over the live 3D scene when the player presses `E` near a city.
 * Provides five tabs:
 *   - Lore     : AI-generated colony name + description (read-only)
 *   - Trade    : Sell cargo at city (reuses /api/ship/sell)
 *   - Refit    : Repair HP / shield / oxygen → /api/city/refuel
 *   - Shop     : Buy ammo / cargo / shield upgrades → /api/city/buy
 *   - Contracts: List/accept/turn-in/abandon → /api/city/contracts(/*)
 *
 * Lifecycle: parent (Explore3D) toggles `open`, pauses scene input, and
 * refreshes the persistent player state + inventory + credits after the
 * overlay closes (in case anything changed).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
  Building2, X, Coins, Heart, Shield, Wind, Wrench, ShoppingBag,
  ScrollText, Wallet, ArrowRight, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { toastRepChanges } from '../lib/factionToast';

const TABS = [
  { id: 'lore', label: 'Lore', icon: ScrollText },
  { id: 'trade', label: 'Trade', icon: Wallet },
  { id: 'refit', label: 'Refit', icon: Wrench },
  { id: 'shop', label: 'Shop', icon: ShoppingBag },
  { id: 'contracts', label: 'Contracts', icon: Sparkles },
];

const SHOP_LINES = [
  {
    id: 'ammo_pack',
    label: 'Energy Cells (pistol)',
    detail: '+30 reserve ammo · stack of 10 max',
    cost: 60,
  },
  {
    id: 'cargo_upgrade',
    label: 'Cargo Hold Expansion',
    detail: '+25 cargo capacity',
    cost: 850,
  },
  {
    id: 'shield_upgrade',
    label: 'Shield Capacitor',
    detail: '+15 max shield',
    cost: 1200,
  },
  {
    id: 'nanites_pack',
    label: 'Nanite Pack (10)',
    detail: '+10 nanites for building structures',
    cost: 50,
  },
];

export function CityOverlay({ open, onClose, city, onAfterAction, initialTab }) {
  const { api, user, updateCredits } = useAuth();
  const [tab, setTab] = useState('lore');
  const [playerState, setPlayerState] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [contracts, setContracts] = useState({ available: [], active: null });
  const [busy, setBusy] = useState(false);
  // Phase A — controlling faction + my standing at this system
  const [factionInfo, setFactionInfo] = useState(null);

  const coords_key = city?.coords_key;
  const planet_idx = city?.planet_idx;
  const cityName = city?.name || 'Colony';

  const refreshAll = useCallback(async () => {
    if (!open || !city) return;
    try {
      const [s, i, c] = await Promise.all([
        api.get('/api/player/state').then((r) => r.data),
        api.get('/api/player/inventory').then((r) => r.data),
        api.get(`/api/city/contracts?coords_key=${encodeURIComponent(coords_key)}&planet_idx=${planet_idx}`)
          .then((r) => r.data),
      ]);
      setPlayerState(s);
      setInventory(i);
      setContracts(c || { available: [], active: null });
      api.get(`/api/factions/system?coords_key=${encodeURIComponent(coords_key)}`)
        .then((r) => setFactionInfo(r.data)).catch(() => {});
    } catch (_e) {
      toast.error('Failed to load city data');
    }
  }, [api, open, city, coords_key, planet_idx]);

  useEffect(() => {
    if (open) refreshAll();
    if (open) setTab(initialTab || 'lore');
  }, [open, refreshAll, initialTab]);

  // ------------ Actions ------------

  const handleSell = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/api/ship/sell', {
        // Phase 12d — selling from inside the city flags this as an "at-city"
        // transaction so trade_route contracts can progress.
        at_city_coords_key: coords_key,
        at_city_planet_idx: planet_idx,
      });
      if (data?.credits != null) updateCredits(data.credits);
      toast.success(`Cargo sold · +${Math.round(data?.total_paid || 0)} cr${data?.faction_bonus > 0 ? ` (incl. +${Math.round(data.faction_bonus)} faction bonus)` : ''}`);
      toastRepChanges(data?.rep_changes);
      await refreshAll();
      onAfterAction?.('sell');
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Sell failed';
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  }, [api, coords_key, planet_idx, updateCredits, refreshAll, onAfterAction]);

  const handleRefuel = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/api/city/refuel', {
        coords_key, planet_idx,
      });
      if (data?.credits != null) updateCredits(data.credits);
      toast.success(`Refit complete · −${data?.cost || 0} cr`);
      await refreshAll();
      onAfterAction?.('refuel');
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Refit failed';
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  }, [api, coords_key, planet_idx, updateCredits, refreshAll, onAfterAction]);

  const handleBuy = useCallback(async (item_id) => {
    setBusy(true);
    try {
      // Phase 11 — nanites are a separate economy; route them to the
      // dedicated nanites endpoint instead of the generic city shop.
      if (item_id === 'nanites_pack') {
        const { data } = await api.post('/api/nanites/buy-credits', { packs: 1 });
        if (data?.credits != null) updateCredits(data.credits);
        toast.success(`+${data?.nanites_gained || 10} nanites · −${data?.cost_credits || 50} cr`);
        await refreshAll();
        onAfterAction?.('buy-nanites');
        return;
      }
      const { data } = await api.post('/api/city/buy', { item_id, qty: 1, coords_key });
      if (data?.credits != null) updateCredits(data.credits);
      toast.success(`Purchased · −${data?.cost || 0} cr`);
      await refreshAll();
      onAfterAction?.('buy');
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Purchase failed';
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  }, [api, coords_key, updateCredits, refreshAll, onAfterAction]);

  const handleAcceptContract = useCallback(async (contract) => {
    setBusy(true);
    try {
      const { data } = await api.post('/api/city/contracts/accept', {
        contract_id: contract.id,
        coords_key, planet_idx,
      });
      setContracts(data || { available: [], active: null });
      toast.success('Contract accepted');
      onAfterAction?.('contract-accepted');
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Could not accept contract';
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  }, [api, coords_key, planet_idx, onAfterAction]);

  const handleAbandon = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/api/city/contracts/abandon', {
        coords_key, planet_idx,
      });
      setContracts(data || { available: [], active: null });
      toast.success('Contract abandoned');
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed to abandon';
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  }, [api, coords_key, planet_idx]);

  const handleTurnIn = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/api/city/contracts/turnin', {
        coords_key, planet_idx,
      });
      if (data?.credits != null) updateCredits(data.credits);
      toast.success(`Contract complete · +${data?.reward || 0} cr`);
      toastRepChanges(data?.rep_changes);
      await refreshAll();
      onAfterAction?.('contract-turnin');
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Cannot turn in yet';
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  }, [api, coords_key, planet_idx, updateCredits, refreshAll, onAfterAction]);

  // What can be SOLD at the city kiosk is what's in the ship's cargo hold —
  // the player's backpack is private and must be stowed at the ship first.
  const cargoUsed = useMemo(
    () => (inventory?.ship_cargo || []).reduce((a, o) => a + (o.qty || 0), 0),
    [inventory],
  );
  const cargoCap = inventory?.cargo_capacity ?? 50;
  const credits = user?.credits ?? 0;

  if (!open || !city) return null;

  return (
    <div
      className="fixed inset-0 z-[2100] flex items-center justify-center"
      data-testid="city-overlay"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-[#02060f]/85 backdrop-blur-md" />

      <div
        className="relative w-[min(1040px,96vw)] max-h-[90vh] overflow-y-auto rounded-2xl border border-amber-300/30 bg-[#040912]/95 shadow-[0_0_0_1px_rgba(252,211,77,0.18)] p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="city-overlay-title"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-5">
          <div className="flex items-center gap-3">
            <Building2 className="w-7 h-7 text-amber-300" />
            <div>
              <h2
                id="city-overlay-title"
                className="text-2xl font-bold text-slate-100"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                data-testid="city-overlay-title"
              >
                {cityName}
              </h2>
              <p className="text-[11px] text-amber-300/70 tracking-[0.2em] uppercase mt-0.5">
                Colony Hub · press <span className="text-amber-200">Esc</span> to leave
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="border-amber-300/40 text-amber-200 bg-amber-900/10" data-testid="city-credits-badge">
              <Coins className="w-3.5 h-3.5 mr-1" /> {credits} cr
            </Badge>
            <Button variant="outline" size="sm" onClick={onClose} data-testid="city-overlay-close">
              <X className="w-4 h-4 mr-2" /> Close
            </Button>
          </div>
        </div>

        {/* Phase A — controlling faction banner */}
        {factionInfo?.faction && (
          <div
            className="flex items-center justify-between gap-3 rounded-lg border px-4 py-2.5 mb-4"
            style={{
              backgroundColor: `${factionInfo.faction.color}10`,
              borderColor: `${factionInfo.faction.color}45`,
            }}
            data-testid="city-faction-banner"
          >
            <div className="text-sm">
              <span className="font-semibold" style={{ color: factionInfo.faction.color }}>
                {factionInfo.faction.name}
              </span>
              <span className="text-slate-400"> controls this colony</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span
                className="uppercase tracking-wider px-2 py-0.5 rounded border font-semibold"
                style={{ color: factionInfo.faction.color, borderColor: `${factionInfo.faction.color}55` }}
                data-testid="city-faction-standing"
              >
                {factionInfo.standing?.tier}
              </span>
              {factionInfo.standing?.buy_discount > 0 && (
                <span className="text-emerald-300">
                  −{Math.round(factionInfo.standing.buy_discount * 100)}% prices · +{Math.round(factionInfo.standing.sell_bonus * 100)}% sells
                </span>
              )}
              {factionInfo.standing?.refused && (
                <span className="text-red-400 font-semibold">SERVICE REFUSED</span>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-5 border-b border-slate-800/60 pb-3">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`city-tab-${t.id}`}
                className={[
                  'px-3 py-1.5 rounded-md text-sm tracking-wide flex items-center gap-2 transition-colors',
                  active
                    ? 'bg-amber-500/15 text-amber-200 border border-amber-300/40'
                    : 'text-slate-400 hover:text-slate-200 border border-transparent hover:border-slate-700/60',
                ].join(' ')}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Body */}
        {tab === 'lore' && (
          <LorePanel city={city} />
        )}

        {tab === 'trade' && (
          <TradePanel
            inventory={inventory}
            cargoUsed={cargoUsed}
            cargoCap={cargoCap}
            onSell={handleSell}
            busy={busy}
          />
        )}

        {tab === 'refit' && (
          <RefitPanel
            playerState={playerState}
            credits={credits}
            onRefuel={handleRefuel}
            busy={busy}
          />
        )}

        {tab === 'shop' && (
          <ShopPanel
            playerState={playerState}
            inventory={inventory}
            credits={credits}
            onBuy={handleBuy}
            busy={busy}
          />
        )}

        {tab === 'contracts' && (
          <ContractsPanel
            data={contracts}
            onAccept={handleAcceptContract}
            onAbandon={handleAbandon}
            onTurnIn={handleTurnIn}
            busy={busy}
          />
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Tab Panels
// ============================================================================

function LorePanel({ city }) {
  return (
    <div className="space-y-4" data-testid="city-lore-panel">
      <div className="rounded-lg border border-amber-300/20 bg-amber-900/10 p-5">
        <p className="text-slate-200 leading-relaxed text-[15px]">
          {city.description || 'A small colony serving frontier traffic.'}
        </p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
        <Stat label="Buildings" value={(city.buildings || []).filter((b) => b.kind !== 'pad').length} />
        <Stat label="Plaza Radius" value={`${(city.plaza_radius || 0).toFixed(1)} m`} />
        <Stat label="Landing Pad" value={`${(city.landing_pad_radius || 0).toFixed(1)} m`} />
        <Stat label="Seed" value={`#${city.seed || 0}`} />
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-slate-800/60 bg-slate-900/40 px-3 py-2">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{label}</div>
      <div className="text-slate-200 mt-0.5">{value}</div>
    </div>
  );
}

function TradePanel({ inventory, cargoUsed, cargoCap, onSell, busy }) {
  // City kiosk sells the SHIP's cargo hold, not the player's backpack.
  const ores = inventory?.ship_cargo || [];
  const backpackUnits = (inventory?.ores || []).reduce((a, o) => a + (o.qty || 0), 0);
  const totalEst = ores.reduce(
    (a, o) => a + (Number(o.qty || 0) * Number(o.est_value_per_unit || 0)),
    0,
  );
  return (
    <div className="space-y-4" data-testid="city-trade-panel">
      <div className="flex items-center justify-between rounded-md border border-slate-800/60 bg-slate-900/40 px-4 py-3">
        <div className="text-sm text-slate-300">
          Ship Cargo: <span className="text-slate-100 font-mono">{cargoUsed.toFixed(1)} / {cargoCap.toFixed(0)} u</span>
        </div>
        <div className="text-sm text-slate-300">
          Est. Value: <span className="text-amber-200 font-mono">{Math.round(totalEst)} cr</span>
        </div>
      </div>
      {ores.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-700/60 px-4 py-8 text-center text-slate-500 text-sm">
          {backpackUnits > 0 ? (
            <>
              Ship cargo hold is empty. You&apos;re carrying{' '}
              <span className="text-amber-200 font-mono">{backpackUnits.toFixed(1)} u</span>
              {' '}in your backpack — return to your ship and press
              <span className="text-amber-200"> G </span>to stow it before selling.
            </>
          ) : (
            <>Ship cargo hold is empty. Mine deposits on the surface, stow them at your ship, then return to sell.</>
          )}
        </div>
      ) : (
        <div className="grid gap-2">
          {ores.map((o) => (
            <div key={o.symbol} className="flex items-center justify-between rounded-md border border-slate-800/60 bg-slate-900/40 px-4 py-2">
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="border-slate-600 text-slate-300 font-mono">{o.symbol}</Badge>
                <span className="text-slate-200">{o.name}</span>
              </div>
              <div className="text-sm text-slate-400 font-mono">
                {Number(o.qty || 0).toFixed(1)} u · {Math.round((o.qty || 0) * (o.est_value_per_unit || 0))} cr
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-end">
        <Button
          onClick={onSell}
          disabled={busy || ores.length === 0}
          data-testid="city-sell-all-btn"
          className="bg-amber-500/90 hover:bg-amber-400 text-slate-950 font-semibold"
        >
          <Coins className="w-4 h-4 mr-2" />
          Sell All Cargo
        </Button>
      </div>
    </div>
  );
}

function VitalBar({ label, value, max, color, Icon }) {
  const pct = max ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.18em] text-slate-400 mb-1">
        <div className="flex items-center gap-1.5">
          <Icon className={`w-3.5 h-3.5 ${color}`} />
          <span>{label}</span>
        </div>
        <span className="font-mono text-slate-300">{Math.round(value)} / {Math.round(max)}</span>
      </div>
      <Progress value={pct} className="h-2 bg-slate-900/80" />
    </div>
  );
}

function RefitPanel({ playerState, credits, onRefuel, busy }) {
  const hp = playerState?.hp ?? 100;
  const maxHp = playerState?.max_hp ?? 100;
  const shield = playerState?.shield ?? 0;
  const maxShield = playerState?.max_shield ?? 50;
  const oxy = playerState?.oxygen ?? 100;
  const maxOxy = playerState?.max_oxygen ?? 100;
  const isFull = hp >= maxHp && shield >= maxShield && oxy >= maxOxy;

  // Estimated cost (matches backend tuning, capped 25..750)
  const estRaw = (maxHp - hp) * 0.8 + (maxShield - shield) * 1.5 + (maxOxy - oxy) * 0.4;
  const estCost = isFull ? 0 : Math.max(25, Math.min(750, Math.round(estRaw)));

  return (
    <div className="space-y-4" data-testid="city-refit-panel">
      <div className="grid sm:grid-cols-3 gap-3">
        <VitalBar label="Hull" value={hp} max={maxHp} color="text-rose-400" Icon={Heart} />
        <VitalBar label="Shield" value={shield} max={maxShield} color="text-cyan-300" Icon={Shield} />
        <VitalBar label="Oxygen" value={oxy} max={maxOxy} color="text-sky-300" Icon={Wind} />
      </div>
      <div className="flex items-center justify-between rounded-md border border-slate-800/60 bg-slate-900/40 px-4 py-3">
        <div className="text-sm text-slate-300">
          Refit cost: <span className="text-amber-200 font-mono">{isFull ? 0 : estCost} cr</span>
        </div>
        <div className="text-xs text-slate-500">Credits: <span className="text-slate-300 font-mono">{credits} cr</span></div>
      </div>
      <div className="flex justify-end">
        <Button
          onClick={onRefuel}
          disabled={busy || isFull || credits < estCost}
          data-testid="city-refuel-btn"
          className="bg-cyan-500/90 hover:bg-cyan-400 text-slate-950 font-semibold"
        >
          <Wrench className="w-4 h-4 mr-2" />
          {isFull ? 'Fully Repaired' : 'Refit Now'}
        </Button>
      </div>
    </div>
  );
}

function ShopPanel({ playerState, inventory, credits, onBuy, busy }) {
  return (
    <div className="space-y-3" data-testid="city-shop-panel">
      {SHOP_LINES.map((item) => {
        const affordable = credits >= item.cost;
        return (
          <div
            key={item.id}
            className="flex items-center justify-between rounded-md border border-slate-800/60 bg-slate-900/40 px-4 py-3"
          >
            <div>
              <div className="text-slate-100 font-medium">{item.label}</div>
              <div className="text-xs text-slate-500 mt-0.5">{item.detail}</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-sm text-amber-200 font-mono">{item.cost} cr</div>
              <Button
                onClick={() => onBuy(item.id)}
                disabled={busy || !affordable}
                size="sm"
                data-testid={`city-buy-${item.id}`}
                className="bg-amber-500/90 hover:bg-amber-400 text-slate-950 font-semibold disabled:bg-slate-700 disabled:text-slate-400"
              >
                Buy
              </Button>
            </div>
          </div>
        );
      })}
      <div className="text-xs text-slate-500 pt-2">
        Ammo reserve: <span className="font-mono text-slate-300">{playerState?.pistol_reserve_ammo ?? 0}</span> ·
        Cargo cap: <span className="font-mono text-slate-300">{inventory?.cargo_capacity ?? 50} u</span> ·
        Max shield: <span className="font-mono text-slate-300">{playerState?.max_shield ?? 50}</span>
      </div>
    </div>
  );
}

function ContractsPanel({ data, onAccept, onAbandon, onTurnIn, busy }) {
  const available = data?.available || [];
  const active = data?.active;
  return (
    <div className="space-y-4" data-testid="city-contracts-panel">
      {/* Active contract banner */}
      {active && (
        <div className="rounded-lg border border-emerald-400/30 bg-emerald-900/10 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-300/80">Active Contract</div>
              <div className="text-slate-100 font-semibold mt-0.5">{active.contract?.title}</div>
              <div className="text-sm text-slate-400 mt-1">{active.contract?.description}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-xs text-slate-400 uppercase tracking-[0.18em]">Reward</div>
              <div className="text-amber-200 font-mono text-lg">+{active.contract?.reward} cr</div>
            </div>
          </div>
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
              <span>Progress</span>
              <span className="font-mono">
                {(active.progress || 0).toFixed(active.contract?.type === 'kill_hostiles' ? 0 : 1)}
                {' / '}
                {(active.contract?.target || 0).toFixed(active.contract?.type === 'kill_hostiles' ? 0 : 0)}
              </span>
            </div>
            <Progress
              value={Math.min(100, ((active.progress || 0) / (active.contract?.target || 1)) * 100)}
              className="h-2 bg-slate-900/80"
            />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button
              onClick={onAbandon}
              disabled={busy}
              variant="outline"
              size="sm"
              data-testid="city-contract-abandon"
            >
              Abandon
            </Button>
            <Button
              onClick={onTurnIn}
              disabled={busy || (active.progress || 0) < (active.contract?.target || Infinity)}
              size="sm"
              data-testid="city-contract-turnin"
              className="bg-emerald-500/90 hover:bg-emerald-400 text-slate-950 font-semibold"
            >
              Turn In
            </Button>
          </div>
        </div>
      )}

      {/* Available contracts list */}
      <div className="space-y-2">
        {available.length === 0 && (
          <div className="text-sm text-slate-500 px-3 py-6 text-center">No contracts posted right now.</div>
        )}
        {available.map((c) => (
          <div
            key={c.id}
            className={c.faction_id
              ? 'rounded-md border border-yellow-400/50 bg-yellow-900/10 px-4 py-3 shadow-[0_0_18px_rgba(250,204,21,0.10)]'
              : 'rounded-md border border-slate-800/60 bg-slate-900/40 px-4 py-3'}
            data-testid={c.faction_id ? 'city-contract-faction' : `city-contract-${c.type}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                {c.faction_id && (
                  <div className="text-[10px] uppercase tracking-[0.22em] text-yellow-300 mb-1">
                    ★ Faction Exclusive · +{c.bonus_rep} bonus rep
                  </div>
                )}
                <div className="text-slate-100 font-medium">{c.title}</div>
                <div className="text-sm text-slate-400 mt-1">{c.description}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs text-slate-400 uppercase tracking-[0.18em]">Reward</div>
                <div className="text-amber-200 font-mono">+{c.reward} cr</div>
              </div>
            </div>
            <div className="flex justify-end mt-3">
              <Button
                onClick={() => onAccept(c)}
                disabled={busy || !!active}
                size="sm"
                className="bg-amber-500/90 hover:bg-amber-400 text-slate-950 font-semibold disabled:bg-slate-700 disabled:text-slate-400"
              >
                Accept <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default CityOverlay;
